/**
 * DrivePickerButton — public API for "open a Drive folder picker".
 *
 * History
 * -------
 *   v1: Used Google's official Drive Picker iframe (gapi.load('picker')).
 *       Worked great for Workspace tenants that allow third-party iframe
 *       embeds, but `blackrockcolorado.com` (and likely other tenants
 *       with stricter org policies) returns a bare HTTP 403 from
 *       `docs.google.com/picker` before any of our code runs. Verified
 *       against multiple tenants — the response is just Google's
 *       standard "403. That's an error" page.
 *
 *   v2 (this file): Internally renders our self-hosted FolderFinder
 *       modal (built on top of our backend's Drive REST endpoints).
 *       Identical functional surface — browse, search, recent, starred,
 *       shared with me, shared drives, paste-by-URL — but works in
 *       every tenant because there's no Google iframe involved.
 *
 * The component / hook signatures are kept identical to v1 so existing
 * consumers (UploadDialog, JobDetailPage, ChangeOrderRequestSection,
 * DriveFolderTree, MediaSidebar) require no changes.
 */
import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Folder, FolderOpen } from 'lucide-react';
import FolderFinder from '@/components/media/FolderFinder';

/**
 * Imperative hook — open the picker programmatically.
 * Mounts a hidden FolderFinder controlled by `open` state and toggles it
 * when the consumer calls `openPicker()`.
 *
 * Note: callers must render `<picker />` (returned by this hook) into
 * their tree, OR they can use the `<DrivePickerButton/>` component
 * which handles the rendering for them.
 *
 * For backwards compat with the v1 Google Picker hook (which managed
 * its own DOM), we expose a `<HiddenPickerHost />` element that any
 * caller can drop into JSX without a key prop.
 */
export function useDrivePicker({
  onPicked,
  initialFolderId,
  title,
  // mode: 'folders' (default) | 'files'
  // - 'folders': user picks a single folder, callback receives one obj
  // - 'files': user picks a folder, callback receives an array containing
  //   ONE pseudo-file entry representing the folder; UploadDialog detects
  //   this and routes to its folder-import flow. We don't have a true
  //   file-multiselect picker without Google's iframe; folder-import is
  //   the right primitive (the entire folder gets imported as link-only
  //   media in one click — better UX than picking files one-by-one).
  mode = 'folders',
} = {}) {
  const [open, setOpen] = useState(false);
  const openPicker = useCallback(() => setOpen(true), []);

  const handleConfirm = useCallback((folderId, folderName, fullPath) => {
    setOpen(false);
    if (!folderId) return;
    const payload = {
      id: folderId,
      name: folderName,
      mimeType: 'application/vnd.google-apps.folder',
      parents: [],
      isShared: false,
      url: `https://drive.google.com/drive/folders/${folderId}`,
      path: fullPath,
    };
    if (mode === 'files') {
      onPicked?.([payload]);
    } else {
      onPicked?.(payload);
    }
  }, [mode, onPicked]);

  const pickerEl = (
    <FolderFinder
      open={open}
      onOpenChange={setOpen}
      title={title || (mode === 'files' ? 'Pick a Drive folder to import' : 'Pick a Google Drive folder')}
      cta={mode === 'files' ? 'Import this folder' : 'Use this folder'}
      initialSelectedId={initialFolderId || null}
      onConfirm={handleConfirm}
    />
  );

  return {
    openPicker,
    loading: false,
    // Expose the picker element so the consumer can render it. We always
    // return it; consumers that don't render it just won't see the modal.
    picker: pickerEl,
  };
}

/**
 * Convenience component — renders a button that, when clicked, opens
 * the Drive folder picker. Used as the "Drive folder" pill in
 * JobDetailPage, the "Pick folder" affordance in UploadDialog, and
 * the per-CO-Request photo destination chooser.
 */
export default function DrivePickerButton({
  label,
  onPicked,
  initialFolderId,
  title,
  testid = 'drive-picker',
  disabled = false,
  variant = 'pill',
}) {
  const { openPicker, picker } = useDrivePicker({ onPicked, initialFolderId, title });

  if (variant === 'pill') {
    return (
      <>
        <button
          type="button"
          data-testid={`${testid}-button`}
          disabled={disabled}
          onClick={openPicker}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border border-border bg-background text-muted-foreground hover:border-[#4575A8]/60 hover:bg-[#4575A8]/5 hover:text-white transition-colors disabled:opacity-50"
        >
          <FolderOpen className="w-3 h-3 text-[#4575A8]" />
          {label || 'Pick folder'}
        </button>
        {picker}
      </>
    );
  }

  return (
    <>
      <Button
        type="button"
        data-testid={`${testid}-button`}
        disabled={disabled}
        onClick={openPicker}
        variant="outline"
        size="sm"
        className="border-border text-muted-foreground hover:bg-muted/40"
      >
        <Folder className="w-3.5 h-3.5 mr-1.5" />
        {label || 'Pick folder'}
      </Button>
      {picker}
    </>
  );
}

/**
 * Pre-warm the Drive picker — no-op since we now use our own modal
 * (no external SDK to download). Kept as a named export so existing
 * `prewarmDrivePicker()` calls in `Layout.js` etc. don't fail.
 */
export function prewarmDrivePicker() {
  /* no-op: FolderFinder loads on first open via Drive REST endpoints */
}
